import { useState, useEffect, useCallback } from 'react';
import {
  getWorkshopDetails,
  getCountdownTargetDate,
  isUpdateTime
} from '../utils/workshopScheduler';

/**
 * Custom hook for managing workshop schedule
 * Automatically updates every Sunday at 12:00 AM
 */
export function useWorkshopSchedule() {
  const [workshopDetails, setWorkshopDetails] = useState(() => getWorkshopDetails());
  const [lastUpdateTime, setLastUpdateTime] = useState(() => Date.now());

  // Update workshop details
  const updateWorkshopDetails = useCallback(() => {
    const newDetails = getWorkshopDetails();
    setWorkshopDetails(newDetails);
    setLastUpdateTime(Date.now());
    
    // Store in localStorage for persistence
    localStorage.setItem('workshopDetails', JSON.stringify({
      details: newDetails,
      lastUpdate: Date.now()
    }));
    
    console.log('Workshop schedule updated:', newDetails);
  }, []);

  // Check for stored workshop details on mount
  useEffect(() => {
    const stored = localStorage.getItem('workshopDetails');
    if (stored) {
      try {
        const { details, lastUpdate } = JSON.parse(stored);
        const timeSinceUpdate = Date.now() - lastUpdate;
        const oneWeek = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
        
        // If stored data is less than a week old, use it
        if (timeSinceUpdate < oneWeek) {
          setWorkshopDetails(details);
          setLastUpdateTime(lastUpdate);
        } else {
          // Update if stored data is old
          updateWorkshopDetails();
        }
      } catch (error) {
        console.warn('Error parsing stored workshop details:', error);
        updateWorkshopDetails();
      }
    }
  }, [updateWorkshopDetails]);

  // Set up automatic update timer
  useEffect(() => {
    const checkForUpdate = () => {
      if (isUpdateTime()) {
        updateWorkshopDetails();
      }
    };

    // Check every minute for update time
    const interval = setInterval(checkForUpdate, 60000);
    
    // Also check on component mount
    checkForUpdate();

    return () => clearInterval(interval);
  }, [updateWorkshopDetails]);

  // Manual update function
  const forceUpdate = useCallback(() => {
    updateWorkshopDetails();
  }, [updateWorkshopDetails]);

  return {
    workshopDetails,
    lastUpdateTime,
    forceUpdate,
    countdownTarget: getCountdownTargetDate()
  };
}